/**
 * Persistent block-list records — IndexedDB, one record per blocked userId,
 * ts-monotonic LWW apply.
 *
 * The deniable-block property is enforced by absence at the type level:
 * there is NO method here that the encrypt path could consult to alter an
 * outbound message based on "is this peer blocked." Outbound messages to a
 * blocked peer go through the unchanged encrypt+ship path so a passive
 * observer (or the recipient, who is the adversary in this model) cannot
 * detect the block.
 *
 * Sync rule:
 *   apply(payload) writes only if payload.ts > current.blockedAt for a
 *   .block, or only if payload.ts > current.blockedAt for an .unblock.
 *   Equal or older ts is ignored. This means a hostile server replaying
 *   an OLDER .unblock after we just landed a NEWER .block cannot resurrect
 *   the peer: the .block (newer ts) wins on next sync.
 *
 * SECURITY CONTRACT: the caller MUST have verified the outer envelope's
 * Ed25519 signature against our own identity key BEFORE passing a payload
 * to `apply`. The store cannot re-check that signature here because it has
 * no access to the envelope context.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/BlockListStore.swift`.
 */

import type {
  BlockListEntry,
  BlockReason,
  BlockSyncPayload,
} from "./BlockListEntry";
import { BLOCK_SYNC_SCHEMA_VERSION } from "./BlockListEntry";

// ---------------------------------------------------------------------------
// IndexedDB constants
// ---------------------------------------------------------------------------

const DB_VERSION = 1;
const STORE_NAME = "block_list";

// ---------------------------------------------------------------------------
// IndexedDB plumbing
// ---------------------------------------------------------------------------

function openDb(dbName: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(dbName, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        // keyed by userId — one record per blocked peer
        db.createObjectStore(STORE_NAME, { keyPath: "userId" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function idbGet(
  db: IDBDatabase,
  userId: string,
): Promise<BlockListEntry | undefined> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const req = tx.objectStore(STORE_NAME).get(userId);
    req.onsuccess = () => resolve(req.result as BlockListEntry | undefined);
    req.onerror = () => reject(req.error);
  });
}

function idbPut(db: IDBDatabase, entry: BlockListEntry): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const req = tx.objectStore(STORE_NAME).put(entry);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

function idbDelete(db: IDBDatabase, userId: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const req = tx.objectStore(STORE_NAME).delete(userId);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

function idbGetAll(db: IDBDatabase): Promise<BlockListEntry[]> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const req = tx.objectStore(STORE_NAME).getAll();
    req.onsuccess = () => resolve(req.result as BlockListEntry[]);
    req.onerror = () => reject(req.error);
  });
}

function idbClear(db: IDBDatabase): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const req = tx.objectStore(STORE_NAME).clear();
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

// ---------------------------------------------------------------------------
// BlockListStore
// ---------------------------------------------------------------------------

const SHARED_DB_NAME = "kaether:block-list:v1";

/**
 * IndexedDB-backed block-list store.
 *
 * All methods are async because every read/write goes through IndexedDB.
 * For test isolation, pass a unique `dbName` per test case.
 *
 * There is intentionally NO method for the encrypt path to consult —
 * see the deniable-blocking invariant in the file header.
 */
export class BlockListStore {
  private readonly dbName: string;
  private dbPromise: Promise<IDBDatabase> | null = null;

  constructor(dbName: string = SHARED_DB_NAME) {
    this.dbName = dbName;
  }

  private db(): Promise<IDBDatabase> {
    if (!this.dbPromise) {
      this.dbPromise = openDb(this.dbName);
    }
    return this.dbPromise;
  }

  // -------------------------------------------------------------------------
  // Local actions
  // -------------------------------------------------------------------------

  /**
   * Local user blocks a peer. Persists the entry and returns the payload
   * the caller should ship over the existing pairwise E2EE channel to
   * each of the user's other linked devices.
   */
  async block(
    userId: string,
    ts: number = Math.floor(Date.now() / 1000),
    reason: BlockReason = "userInitiated",
  ): Promise<BlockSyncPayload> {
    const db = await this.db();
    const entry: BlockListEntry = { userId, blockedAt: ts, reason };
    await idbPut(db, entry);
    return {
      schema: BLOCK_SYNC_SCHEMA_VERSION,
      blockedUserId: userId,
      action: "block",
      ts,
      reason,
    };
  }

  /**
   * Local user unblocks a peer. Removes the entry and returns the sync
   * payload caller ships to own devices.
   */
  async unblock(
    userId: string,
    ts: number = Math.floor(Date.now() / 1000),
  ): Promise<BlockSyncPayload> {
    const db = await this.db();
    await idbDelete(db, userId);
    return {
      schema: BLOCK_SYNC_SCHEMA_VERSION,
      blockedUserId: userId,
      action: "unblock",
      ts,
      reason: null,
    };
  }

  /**
   * Apply a peer-device sync message. Last-writer-wins by ts: writes only
   * when the payload's ts is strictly newer than the locally-known state.
   * Equal or older ts are ignored. Returns true iff local state changed.
   */
  async apply(payload: BlockSyncPayload): Promise<boolean> {
    const db = await this.db();
    const existing = await idbGet(db, payload.blockedUserId);

    switch (payload.action) {
      case "block": {
        if (existing !== undefined) {
          // Already blocked — only overwrite if strictly newer.
          if (payload.ts <= existing.blockedAt) return false;
        }
        // Not blocked locally → install, or strictly newer → overwrite.
        const entry: BlockListEntry = {
          userId: payload.blockedUserId,
          blockedAt: payload.ts,
          reason: payload.reason ?? "userInitiated",
        };
        await idbPut(db, entry);
        return true;
      }

      case "unblock": {
        if (existing === undefined) return false;
        // Only unblock if strictly newer than the existing block.
        if (payload.ts <= existing.blockedAt) return false;
        await idbDelete(db, payload.blockedUserId);
        return true;
      }
    }
  }

  // -------------------------------------------------------------------------
  // Queries
  // -------------------------------------------------------------------------

  /** Returns true if `userId` is currently in the block list. */
  async isBlocked(userId: string): Promise<boolean> {
    const db = await this.db();
    return (await idbGet(db, userId)) !== undefined;
  }

  /** Returns the stored entry for `userId`, or undefined if not blocked. */
  async entry(userId: string): Promise<BlockListEntry | undefined> {
    const db = await this.db();
    return idbGet(db, userId);
  }

  /**
   * Every blocked entry, sorted by `blockedAt` descending (newest first).
   * Mirrors iOS `BlockListStore.allBlocked()`.
   */
  async allBlocked(): Promise<BlockListEntry[]> {
    const db = await this.db();
    const all = await idbGetAll(db);
    return all.sort((a, b) => b.blockedAt - a.blockedAt);
  }

  // -------------------------------------------------------------------------
  // Reset
  // -------------------------------------------------------------------------

  /** Drop every stored entry. Used in tests and on sign-out. */
  async resetAll(): Promise<void> {
    const db = await this.db();
    await idbClear(db);
  }
}

// ---------------------------------------------------------------------------
// Shared singleton (production use)
// ---------------------------------------------------------------------------

/**
 * Shared singleton for production code. Tests must instantiate their own
 * `BlockListStore` with a unique `dbName` for isolation.
 */
export const sharedBlockListStore = new BlockListStore();
